import { useCallback, useRef, useState } from 'react';

/**
 * Support-mode first-edit acknowledgement (guardrail c, #326). Before a platform operator
 * makes their FIRST mutation in a given FOREIGN workspace, the UI shows a confirm dialog
 * ("you're about to edit another customer's agent…"). The acknowledgement is remembered
 * PER foreign workspace, session-scoped (sessionStorage), so subsequent edits in that same
 * workspace within the session don't nag. A new tab/session prompts again.
 */

const ackKey = (workspaceId: string): string => `ehe.operator-edit-ack.${workspaceId}`;

/** Whether the operator has already acknowledged editing this foreign workspace this session. */
export function hasOperatorEditAck(workspaceId: string): boolean {
  try {
    return sessionStorage.getItem(ackKey(workspaceId)) === '1';
  } catch {
    // Private mode / disabled storage — fail toward re-prompting (safer for a support edit).
    return false;
  }
}

/** Remember that the operator acknowledged editing this foreign workspace (session-scoped). */
export function setOperatorEditAck(workspaceId: string): void {
  try {
    sessionStorage.setItem(ackKey(workspaceId), '1');
  } catch {
    /* private mode / disabled storage — the confirm will simply prompt again */
  }
}

export interface SupportEditConfirm {
  /**
   * Wrap a mutation trigger. When support mode is ACTIVE and the operator has not yet
   * acknowledged this workspace, the action is deferred and the confirm dialog opens;
   * otherwise the action runs immediately. In non-support mode it is a pass-through.
   */
  guard: (action: () => void) => void;
  /** True while the confirm dialog should be shown (a guarded action is pending). */
  confirmOpen: boolean;
  /** Acknowledge (persist), close the dialog, and run the pending action. */
  confirm: () => void;
  /** Dismiss the dialog and drop the pending action (no edit happens). */
  cancel: () => void;
}

/**
 * Manage the first-edit confirm for a foreign workspace. `active` is true only when the
 * caller is an operator acting on a workspace they don't belong to (support mode); when
 * false, `guard` runs actions straight through with no dialog.
 */
export function useSupportEditConfirm(workspaceId: string, active: boolean): SupportEditConfirm {
  // The pending action lives in a REF (not state) so `confirm` can run it in the event
  // handler AFTER the state updates — never inside a render/updater (which would trip
  // React's "cannot update a component while rendering another" and run edits mid-render).
  const pendingRef = useRef<(() => void) | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const guard = useCallback(
    (action: () => void) => {
      if (active && !hasOperatorEditAck(workspaceId)) {
        pendingRef.current = action;
        setConfirmOpen(true);
        return;
      }
      action();
    },
    [active, workspaceId],
  );

  const confirm = useCallback(() => {
    setOperatorEditAck(workspaceId);
    setConfirmOpen(false);
    const action = pendingRef.current;
    pendingRef.current = null;
    action?.();
  }, [workspaceId]);

  const cancel = useCallback(() => {
    pendingRef.current = null;
    setConfirmOpen(false);
  }, []);

  return { guard, confirmOpen, confirm, cancel };
}
